import React, { useMemo, useState } from 'react';
import { Star, MessageSquare, Tag, Filter, ThumbsUp, ThumbsDown } from 'lucide-react';
import useReviews from '../../../hooks/useReviews';

function Stars({ value, max = 5 }) {
  return (
    <span className="db-stars">
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} size={14} fill={i < value ? '#F59E0B' : 'none'} stroke={i < value ? '#F59E0B' : '#D1D5DB'} />
      ))}
    </span>
  );
}

function RatingBar({ label, count, total }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="db-rating-bar">
      <span className="db-rating-bar-label">{label}</span>
      <div className="db-rating-bar-track">
        <div className="db-rating-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="db-rating-bar-count">{count}</span>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffH < 1) return `il y a ${Math.max(1, Math.floor(diffMs / 60_000))} min`;
  if (diffH < 24) return `il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `il y a ${diffD}j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function ReviewsTab({ crousMap }) {
  const { reviews, stats, tagStats, loading } = useReviews();
  const [filterCrous, setFilterCrous] = useState('all');
  const [filterRating, setFilterRating] = useState('all');

  const filtered = useMemo(() => {
    let list = reviews;
    if (filterCrous !== 'all') list = list.filter((r) => r.crousId === filterCrous);
    if (filterRating !== 'all') list = list.filter((r) => r.rating === Number(filterRating));
    return list;
  }, [reviews, filterCrous, filterRating]);

  const crousOptions = useMemo(() => {
    const ids = new Set(reviews.map((r) => r.crousId));
    return Array.from(ids).sort();
  }, [reviews]);

  if (loading) return <div className="db-empty">Chargement des avis…</div>;

  if (stats.count === 0) {
    return (
      <div className="db-empty">
        <MessageSquare size={28} style={{ marginBottom: 12, color: 'var(--primary)' }} />
        <strong style={{ display: 'block', marginBottom: 6 }}>Aucun avis pour l'instant</strong>
        Les avis des étudiants sur les repas apparaîtront ici dès qu'ils commenceront
        à noter via l'application.
      </div>
    );
  }

  return (
    <div className="db-reviews">
      {/* Summary cards */}
      <div className="db-reviews-summary">
        <div className="db-review-card db-review-card-score">
          <div className="db-review-big-score">{stats.avg.toFixed(1)}</div>
          <Stars value={Math.round(stats.avg)} />
          <span className="db-review-count">{stats.count} avis</span>
        </div>

        <div className="db-review-card">
          <h4>Répartition</h4>
          <div className="db-rating-bars">
            {[5, 4, 3, 2, 1].map((n) => (
              <RatingBar
                key={n}
                label={`${n}★`}
                count={stats.distribution[n - 1]}
                total={stats.count}
              />
            ))}
          </div>
        </div>

        <div className="db-review-card">
          <h4>Tags fréquents</h4>
          {tagStats.length === 0 ? (
            <span className="db-muted">Aucun tag</span>
          ) : (
            <div className="db-tag-cloud">
              {tagStats.map(([tag, count]) => (
                <span key={tag} className="db-tag">
                  <Tag size={11} /> {tag} <small>{count}</small>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="db-review-card">
          <h4>Satisfaction par RU</h4>
          <div className="db-review-crous-list">
            {Object.entries(stats.byCrous)
              .sort((a, b) => b[1].avg - a[1].avg)
              .map(([id, s]) => (
                <div key={id} className="db-review-crous-row">
                  <strong>{(crousMap && crousMap[id]) || id}</strong>
                  <div className="db-review-crous-score">
                    <Stars value={Math.round(s.avg)} />
                    <span>{s.avg.toFixed(1)}</span>
                    <small>({s.count})</small>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="db-reviews-filters">
        <Filter size={14} />
        <select value={filterCrous} onChange={(e) => setFilterCrous(e.target.value)}>
          <option value="all">Tous les RU</option>
          {crousOptions.map((id) => (
            <option key={id} value={id}>{(crousMap && crousMap[id]) || id}</option>
          ))}
        </select>
        <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
          <option value="all">Toutes les notes</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>{n} étoile{n > 1 ? 's' : ''}</option>
          ))}
        </select>
        <span className="db-muted">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</span>
      </div>

      {/* Feed */}
      <div className="db-reviews-feed">
        {filtered.slice(0, 50).map((r) => (
          <div key={r.id} className="db-review-item">
            <div className="db-review-item-head">
              <Stars value={r.rating} />
              <span className="db-review-item-ru">{(crousMap && crousMap[r.crousId]) || r.crousId}</span>
              <span className="db-review-item-time">{formatTime(r.timestamp)}</span>
              {r.mealType && <span className="db-review-item-meal">{r.mealType === 'midi' ? '🌞 Midi' : '🌙 Soir'}</span>}
            </div>
            {r.comment && <p className="db-review-item-comment">{r.comment}</p>}
            {r.tags && r.tags.length > 0 && (
              <div className="db-review-item-tags">
                {r.tags.map((t) => <span key={t} className="db-tag db-tag-sm">{t}</span>)}
              </div>
            )}
            <div className="db-review-item-sentiment">
              {r.rating >= 4 ? <ThumbsUp size={12} className="db-sentiment-pos" /> : r.rating <= 2 ? <ThumbsDown size={12} className="db-sentiment-neg" /> : null}
            </div>
          </div>
        ))}
        {filtered.length > 50 && (
          <div className="db-muted" style={{ textAlign: 'center', padding: '16px 0' }}>
            Affichage des 50 avis les plus récents sur {filtered.length}.
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewsTab;
